import { useState, type FormEvent } from 'react';
import { aboutData } from '@/mocks/about';

export default function AboutContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotField) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9on84qqs38sj6jc7bv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const isSpam = typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam'));
        setFormStatus('error');
        setFormError(isSpam ? '提交失败，请稍后再试。' : (serverMsg || '提交失败，请稍后再试。'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form=""
      className="bg-background-100 rounded-xl border border-background-200/60 p-5 md:p-6"
    >
      <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">发送消息</h3>

      {formStatus === 'success' && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-primary-100 text-primary-700 text-sm">
          消息发送成功！我们会尽快回复您。
        </div>
      )}

      {formStatus === 'error' && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
          {formError}
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
              姓名 <span className="text-accent-500">*</span>
            </label>
            <input
              type="text"
              id="contact-name"
              name="name"
              required
              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-300"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
              邮箱 <span className="text-accent-500">*</span>
            </label>
            <input
              type="email"
              id="contact-email"
              name="email"
              required
              className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-300"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>

        <div>
          <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
            主题 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            id="contact-subject"
            name="subject"
            required
            className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900 placeholder:text-foreground-300"
            placeholder="请输入消息主题"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
            消息内容 <span className="text-accent-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none text-foreground-900 placeholder:text-foreground-300"
            placeholder="请输入您的消息内容（最多500字）"
          />
          <p className="text-[10px] text-foreground-400 mt-1 text-right">最多500字</p>
        </div>

        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="spam-guard"
        />

        <button
          type="submit"
          disabled={formStatus === 'loading'}
          className="w-full px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
        >
          {formStatus === 'loading' ? (
            <>发送中...</>
          ) : (
            <>
              发送消息
              <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                <i className="ri-send-plane-line text-sm"></i>
              </span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}